import "./broadcast.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../hooks/AuthProvider";
import API_BASE_URL from "../../../config/api";
import { Modal, Button } from "react-bootstrap";

const statusColors = {
  queued: "bcQueued",
  processing: "bcProcessing",
  completed: "bcCompleted",
  failed: "bcFailed",
};

function formatDate(ts) {
  if (!ts) return "-";
  const t = Number(ts);
  if (!t) return "-";
  const d = t < 1e12 ? t * 1000 : t;
  return new Date(d).toLocaleString();
}

function Broadcast() {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [image, setImage] = useState("");
  const [actions, setActions] = useState([]);
  const [layout, setLayout] = useState("vertical");
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const { token } = useAuth();

  const fetchBroadcasts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/v1/broadcast`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBroadcasts(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      console.error(err.message);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchBroadcasts(); }, [fetchBroadcasts]);

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/v1/broadcast`, {
        title: title.trim() || undefined,
        message: message.trim(),
        image: image.trim() || undefined,
        actions: actions.length > 0 ? actions : undefined,
        layout: layout
      }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setSentInfo(res.data?.data || res.data);
      setTitle("");
      setMessage("");
      setImage("");
      setActions([]);
      setLayout("vertical");
      setConfirmSend(false);
      fetchBroadcasts();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to send broadcast");
      console.error(err.message);
    }
    setSending(false);
  };

  return (
    <div>
      <div className="bcHeader">
        <div>
          <h3>Global Broadcast</h3>
          <p>Send a global update to every user from SOLOCHAT_OFFICIAL. Messages are delivered to each user's chat history.</p>
        </div>
      </div>

      <div className="bcLayout">
        <div className="bcForm">
          <label>Title <span>(optional)</span></label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. New Update Available"
          />

          <label>Message <span>*</span></label>
          <textarea
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write the announcement that will appear in every user's chat..."
          />

          <label>Image URL <span>(optional)</span></label>
          <input
            type="text"
            value={image}
            onChange={(e) => setImage(e.target.value)}
            placeholder="https://... (message is sent as an image message if provided)"
          />

          <div className="bcActionHeader">
            <label>Buttons <span>(max 3)</span></label>
            {actions.length < 3 && (
              <button className="bcAddBtn" onClick={() => setActions([...actions, { label: "", type: "internal", route: "", url: "" }])}>
                + Add Button
              </button>
            )}
          </div>

          {actions.length > 0 && (
            <div className="bcActionsArea">
              <label>Layout</label>
              <div className="bcLayoutSelect">
                <div className={`bcLayoutOption ${layout === "vertical" ? "active" : ""}`} onClick={() => setLayout("vertical")}>Vertical</div>
                <div className={`bcLayoutOption ${layout === "horizontal" ? "active" : ""}`} onClick={() => setLayout("horizontal")}>Horizontal</div>
              </div>

              {actions.map((action, idx) => (
                <div key={idx} className="bcActionItem">
                  <div className="bcActionHeader">
                    <span>Button {idx + 1}</span>
                    <button className="bcRemoveBtn" onClick={() => setActions(actions.filter((_, i) => i !== idx))}>Remove</button>
                  </div>
                  <div className="bcActionRow">
                    <div>
                      <label>Label</label>
                      <input
                        type="text"
                        value={action.label}
                        onChange={(e) => {
                          const newActions = [...actions];
                          newActions[idx].label = e.target.value;
                          setActions(newActions);
                        }}
                        placeholder="e.g. Open Store"
                      />
                    </div>
                    <div>
                      <label>Type</label>
                      <select
                        style={{ width: "100%", padding: "10px", borderRadius: "10px", border: "1px solid #e4e4ec" }}
                        value={action.type}
                        onChange={(e) => {
                          const newActions = [...actions];
                          newActions[idx].type = e.target.value;
                          setActions(newActions);
                        }}
                      >
                        <option value="internal">App Page</option>
                        <option value="external">Website</option>
                      </select>
                    </div>
                  </div>
                  <div className="bcActionFull">
                    {action.type === "internal" ? (
                      <div>
                        <label>App Route</label>
                        <select
                          style={{ width: "100%", padding: "10px", borderRadius: "10px", border: "1px solid #e4e4ec" }}
                          value={action.route}
                          onChange={(e) => {
                            const newActions = [...actions];
                            newActions[idx].route = e.target.value;
                            setActions(newActions);
                          }}
                        >
                          <option value="">Select a page...</option>
                          <option value="marketplace">Marketplace</option>
                          <option value="payment">Wallet (SoloCoins)</option>
                          <option value="purchase_coin">Buy SoloCoins</option>
                          <option value="sell_coin">Sell SoloCoins</option>
                          <option value="discovery_map">Discovery Map</option>
                          <option value="view_video">Minute Videos</option>
                          <option value="explore_rooms">Explore Rooms</option>
                          <option value="create_group">Create Group</option>
                          <option value="create_room">Create Room</option>
                          <option value="sticker_store">Sticker Store</option>
                          <option value="bot_store">Bot Store</option>
                          <option value="inventory">My Inventory</option>
                          <option value="subscription">Subscription</option>
                          <option value="send_request">Send Request</option>
                          <option value="refer">Refer a Friend</option>
                          <option value="settings">Settings</option>
                          <option value="user_profile">My Profile</option>
                          <option value="global_search">Global Search</option>
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label>Website URL</label>
                        <input
                          type="text"
                          value={action.url}
                          onChange={(e) => {
                            const newActions = [...actions];
                            newActions[idx].url = e.target.value;
                            setActions(newActions);
                          }}
                          placeholder="https://..."
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && <div className="bcError">{error}</div>}

          <button
            className="bcSendBtn"
            disabled={!message.trim() || sending}
            onClick={() => setConfirmSend(true)}
          >
            {sending ? "Sending..." : "Send Broadcast"}
          </button>
        </div>

        <div className="bcSide">
          <div className="bcSideCard">
            <div className="bcSideTitle">How it works</div>
            <ul>
              <li>Writes to <code>/global_broadcasts/{"{broadcastId}"}</code>.</li>
              <li>A background function fans it out to all users in batches of 1000.</li>
              <li>Each user gets a system message from <strong>SOLOCHAT_OFFICIAL</strong>.</li>
              <li>The trigger node is cleaned up after processing.</li>
            </ul>
          </div>
        </div>
      </div>

      {sentInfo && (
        <div className="bcSentBanner">
          <strong>Broadcast sent.</strong>{" "}
          {sentInfo.broadcastId ? `ID: ${sentInfo.broadcastId}` : "It is now being delivered to all users."}
        </div>
      )}

      <div className="bcList">
        <div className="bcListHeader">
          <span>Recent Broadcasts</span>
          <button onClick={fetchBroadcasts}>Refresh</button>
        </div>
        {loading && <div className="bcEmpty"><p>Loading...</p></div>}
        {!loading && broadcasts.length === 0 && (
          <div className="bcEmpty">
            <p>No active broadcasts right now. Processed broadcasts are cleaned up automatically.</p>
          </div>
        )}
        {broadcasts.length > 0 && (
          <div className="bcListRows">
            {broadcasts.map((b) => (
              <div key={b.broadcastId} className="bcRow">
                <div className="bcRowBody">
                  <strong>{b.title || "Broadcast"}</strong>
                  <p>{b.message}</p>
                  {b.image && <span className="bcRowMeta">Image: {b.image}</span>}
                  <small>{b.broadcastId} &middot; {formatDate(b.createdAt)}</small>
                </div>
                <span className={`bcStatus ${statusColors[b.status] || "bcQueued"}`}>
                  {b.status || "queued"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal show={confirmSend} onHide={() => setConfirmSend(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Send Global Broadcast</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will send the following message to <strong>every user</strong> on the platform:
          <br /><br />
          <em>{message.trim()}</em>
          <br /><br />
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmSend(false)}>Cancel</Button>
          <Button variant="primary" disabled={sending} onClick={handleSend}>
            {sending ? "Sending..." : "Confirm Send"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Broadcast;
